import Button from 'components/atoms/button/Button'
import IconButton from 'components/atoms/button/IconButton'
import Form from 'components/atoms/form/Form'
import Icon from 'components/atoms/icon/Icon'
import Input from 'components/atoms/input/Input'
import Label from 'components/atoms/input/input-label/InputLabel'
import PasswordStrengthMeter from 'components/molecules/password-strength-meter/PasswordStrengthMeter'
import Tooltip from 'components/molecules/tooltip/Tooltip'
import { IconType } from 'lib/helpers/constants/iconTypes'
import withModal from 'lib/helpers/hoc/withModal'
import { useHover } from 'lib/hooks/shared/useHover'
import React, { FC, Fragment } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useChangePassword } from 'lib/hooks/mutations/Settings/useChangePassword'
import { useTranslation } from 'react-i18next'


interface IChangePasswordProps {
    toggleModal?: () => void;
}

const validationSchema = Yup.object({
    current_password: Yup.string()
        .required("Current password is required")
        .min(8, "Current password must be at least 8 characters"),
    password: Yup.string()
        .required("New password is required")
        .min(8, "New password must be at least 8 characters")
        .matches(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>_])[A-Za-z\d!@#$%^&*(),.?":{}|<>_]{8,}$/,
            "Must have 1 uppercase, 1 lowercase, 1 number, 1 special character"
            ),
    password_confirmation: Yup.string()
        .required("Please confirm your new password")
        .oneOf([Yup.ref("password")], "Passwords must match"),
});

const ChangePassword: FC<IChangePasswordProps> = ({ toggleModal }) => {

    const { t } = useTranslation()

    const { watch, formState: { errors }, register, handleSubmit } = useForm({
        resolver: yupResolver(validationSchema),
    })

    const { isHovered, onMouseEnter, onMouseLeave } = useHover()

    const password = watch("password", "");

    const isAtLeast8Chars = password.length >= 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[@$!%*?&]/.test(password);


    const { mutateAsync, isLoading } = useChangePassword()

    const SubmitChangePassword = (data: any) => {
        mutateAsync(data).then(() => {
            toggleModal?.()
        })

    }


    return (
        <Form className='space-y-5' onSubmit={handleSubmit(SubmitChangePassword)}>
            <Input
                label="Old Password"
                placeholder={t("Enter your old password")}
                type={"password"}
                error={errors.current_password}
                {...register("current_password")}
            />

            <div className="relative space-y-5" onMouseLeave={onMouseLeave}>
                <div>
                    <Label>
                        {t('New Password')}
                    </Label>
                    <div className="flex items-center gap-3">
                        <div className="flex-1">
                            <Input
                                placeholder={t("Must be at least 8 characters")}
                                type={"password"}
                                error={errors.password}
                                {...register("password")}
                            />
                        </div>

                        <Button onMouseEnter={onMouseEnter} type='button'>
                            <Icon
                                iconType={IconType.ALERT_ICON}
                                className=""
                                color="#D0D5DD"
                            />
                        </Button>
                    </div>
                </div>
                <Tooltip
                    isOpen={isHovered}
                    className="absolute right-0 w-[340px] sm:w-[387px] h-auto bg-white p-[25px] rounded-[25px] shadow-custom z-[99] top-[85px]"
                    content={
                        <Fragment>
                            <h1 className="font-inter-medium text-primaryTextColor mb-[11px]">
                                {t('Security guidelines')}
                            </h1>
                            <ul className="list-disc text-[14px]  ml-4">
                                <li
                                    className={
                                        isAtLeast8Chars
                                            ? "text-[#2DE28B] line-through"
                                            : "text-[#667085]"
                                    }
                                >
                                    {t('Use at least 8 characters')}
                                </li>
                                <li
                                    className={`${hasUpperCase &&
                                        hasLowerCase &&
                                        hasNumber &&
                                        hasSpecialChar
                                        ? "text-[#2DE28B] line-through"
                                        : "text-[#667085]"
                                        }`}
                                >
                                    {t('Mix uppercase, lowercase, numbers, & symbols')}
                                </li>

                            </ul>
                        </Fragment>
                    }
                />
                <PasswordStrengthMeter password={watch("password")} />
            </div>
            <Input
                label="Password Confirmation"
                placeholder={t("Confirm your password")}
                type={"password"}
                error={errors.password_confirmation}
                {...register("password_confirmation")}
            />

            <div className="flex justify-end pt-4">
                <IconButton disabled={isLoading}>
                    {t('Change Password')}
                </IconButton>
            </div>
        </Form>
    )
}

export default withModal(ChangePassword)